import React from 'react';
import styled from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, PSmall } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import { useGetCompany } from '../../../hooks/use-hydra/admin';
import { FormInput, FormLabel } from '../../Form';
import { useParams } from 'react-router-dom';
import Button from '../../elements/Button';
import useAuth from '../../../hooks/use-auth';
import { toast } from 'react-toastify';
import { addCohort } from '../../../api/admin';
import { useMutation } from 'react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: 32px;
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  padding-bottom: 72px;
`;
const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 80vw;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;

const DatePicker = styled(FormInput).attrs({
  type: 'date',
})`
  margin-top: 8px;
`;

export default function AddCohortModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { getTokenSilently } = useAuth();

  const { id } = useParams<{ id: string }>();

  const {
    data: company,
    refetch: refetchCompany,
    isLoading: isLoadingCompany,
  } = useGetCompany(parseInt(id));
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');

  const addCohortMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      await addCohort(token, {
        companyId: id,
        startDate,
        endDate,
      });
    },
    onSuccess: async () => {
      await refetchCompany();
      toast.success('Cohort added successfully');
      onClose();
    },
    onError: () => {
      toast.error('Something went wrong when adding the cohort');
    },
  });

  const minStartDate = dayjs
    .utc()
    .subtract(1, 'month')
    .startOf('week')
    .add(1, 'day') // monday, bc week starts on Sunday
    .format('YYYY-MM-DD');

  const minEndDate = dayjs
    .utc(startDate)
    .add(4, 'week')
    .subtract(1, 'day')
    .startOf('day') // move to previous sunday
    .format('YYYY-MM-DD');

  const areDatesValid =
    startDate &&
    endDate &&
    dayjs.utc(startDate).day() === 1 && // Monday is 1
    dayjs.utc(endDate).day() === 0 && // Sunday is 0
    dayjs.utc(endDate).add(1, 'day').diff(dayjs.utc(startDate), 'week') === 4;

  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <HeaderRow>
        <Header>Add New Cohort to {company?.name}</Header>
        <CloseButton onClick={onClose} />
      </HeaderRow>
      <Content>
        <PSmall>
          For now, cohorts must have 4 weeks, starting on Monday and ending on
          Sunday.
        </PSmall>
        <FormLabel>Start Date</FormLabel>
        <DatePicker
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
          }}
          min={minStartDate}
          step="7"
        />
        <FormLabel>End Date</FormLabel>
        <DatePicker
          isDisabled={!startDate}
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
          }}
          min={minEndDate}
          max={minEndDate}
          step="7"
        />
        <Row>
          <Button
            label="Add Cohort"
            type="primary"
            showIconBeforeLabel
            faIcon="fa-solid fa-save"
            tooltip="Select valid start and end dates for the cohort"
            isDisabled={!areDatesValid || addCohortMutation.isLoading}
            onClick={() => {
              addCohortMutation.mutate();
            }}
            isLoading={addCohortMutation.isLoading || isLoadingCompany}
          />
        </Row>
      </Content>
    </Wrapper>
  );
}
